import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (App-Brain fleet graduation). askBrain sends
// the user's message + this app's declared action catalog to the generic fleet chat brain
// (POST /api/apps/oriora_encore/chat) — ONE billed call returns ONE registered action + a
// reply; this file resolves any name→id references locally (the data lives here) and the
// app executes the action on-device. The original on-device regex router below
// (routeMessage) is kept as the FREE FALLBACK for offline / signed-out / out-of-funds —
// chat never goes dead.
//
// THE FENCE is honored FIRST, on BOTH paths: Encore is observational-only (describes shows
// already attended). Any line steering toward buying / reselling / pricing / recommending is
// refused deterministically via the app's own brain.fenceCheck() — BEFORE either billed call
// (routing OR recap) — no action, no spend. Mirrored again in the "summarize" action's
// description below (defense in depth) and in the server recap prompt (RECAP_SYSTEM_PROMPT).

import { BrainError, fenceCheck, brainCall } from "../brain";
import { store, splitList, parseMoneyToCents, type Show, type NewShow, type ShowPatch } from "../store";
import type { ChatAction, FilterRow } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn { role: "user" | "assistant"; content: string; rows?: FilterRow[]; }
export interface BrainResponse {
  reply: string;         // conversational preface (banned intent is fenced pre-LLM, so the reply is safe to show)
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

const HELP =
  'Try: "log The National at Brixton in London on 2024-05-01", "shows at Brixton", "rate The National 5", "delete …", or "summarize".';

const QUERY_RE = /\b(show|shows|find|list|search|which|filter|how\s+many|all|at|in|by|see|seen)\b/i;

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

/** Forgiving show lookup (FLEET STANDARD, see ./fuzzy) — tolerant of misspellings, dropped words
 *  and word order over the show's identifying text (artists + venue + city), so "the natonal" or
 *  "national brixton" still resolves the right show. Resolution stays entirely client-side. */
async function findShows(term: string): Promise<Show[]> {
  if (!term.trim()) return [];
  const all = await store.listShows({ limit: 50000 });
  const named = all.map((s) => ({ ...s, name: [s.artists.join(" "), s.venue, s.city].filter(Boolean).join(" ") }));
  return findNamedMatches(named, term);
}

/** "Couldn't find X" enriched with the closest shows ("did you mean …?"), labelled by artist(s)/venue. */
async function showsNotFound(term: string): Promise<string> {
  const all = await store.listShows({ limit: 50000 });
  const labelled = all.map((s) => ({ name: s.venue ? `${s.artists.join(", ")} @ ${s.venue}` : s.artists.join(", ") }));
  return notFoundMessage(labelled, term);
}

/**
 * Parse "The National at Brixton in London on 2024-05-01 festival" into a NewShow.
 * Deterministic + minimal (offline fallback only): pulls out a YYYY-MM-DD date, an optional
 * "festival" flag, then splits artists / venue ("at …") / city ("in …"). Requires ≥1 artist
 * (the store's only hard requirement) or returns null.
 */
function parseAdd(raw: string): NewShow | null {
  let s = " " + raw.trim() + " ";
  const out: NewShow = { artists: [] };

  const dateM = s.match(/\s(?:on\s+)?(\d{4}-\d{2}-\d{2})\s/i);
  if (dateM) { out.seen_on = dateM[1]; s = s.replace(dateM[0], " "); }

  if (/\bfestival\b/i.test(s)) { out.is_festival = true; s = s.replace(/\bfestival\b/gi, " "); }

  const atIdx = s.search(/\sat\s/i);
  const inIdx = s.search(/\sin\s/i);
  const marks = [atIdx, inIdx].filter((i) => i >= 0);
  const firstMark = marks.length ? Math.min(...marks) : -1;

  const artistsPart = firstMark >= 0 ? s.slice(0, firstMark) : s;
  let venuePart = "";
  let cityPart = "";
  if (atIdx >= 0) {
    const afterAt = s.slice(atIdx + 4); // skip " at "
    const inAfter = afterAt.search(/\sin\s/i);
    venuePart = inAfter >= 0 ? afterAt.slice(0, inAfter) : afterAt;
  }
  if (inIdx >= 0) cityPart = s.slice(inIdx + 4); // skip " in "

  const artists = splitList(artistsPart);
  if (artists.length === 0) return null;
  out.artists = artists;
  const v = venuePart.trim(); if (v) out.venue = v;
  const c = cityPart.trim(); if (c) out.city = c;
  return out;
}

// Standing instructions (fleet standard): the customer shapes the app through chat; stored
// on-device, injected into every brain call.
const INSTRUCTIONS_KEY = "oriora.encore.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

// ── SERVER CHAT-BRAIN (the graduated path) ──────────────────────────────────────
export const CHAT_ACTIONS = [
  { name: "add_show", description: "Log a live show already attended. Capture EVERYTHING the user mentions — cost, who they went with, and a rating all belong on this one action.", params: { artists: "comma-separated artist names, headliner first (at least one required)", venue: "optional venue name", city: "optional city", seen_on: "optional date YYYY-MM-DD (resolve today/yesterday); defaults to today if omitted", is_festival: "optional true if this was a festival", cost: "optional ticket cost as a plain number in the user's currency (e.g. 65 or 65.50)", companions: "optional comma-separated names of who they went with", rating: "optional 1-5 star rating if the user gives one in range" } },
  { name: "edit_show", description: "Correct or update an already-logged show — its rating, lineup, venue, city, date, cost, who they went with, festival flag, or setlist notes. Identify the show by artist/venue/city text; give at least one field to change.", params: { term: "which show — artist, venue, or city text as the user wrote it", rating: "optional integer 1-5 star rating", artists: "optional comma-separated artist names (headliner first) to correct the lineup", venue: "optional new venue", city: "optional new city", seen_on: "optional date YYYY-MM-DD", cost: "optional ticket cost as a plain number in the user's currency", companions: "optional comma-separated names of who they went with", is_festival: "optional true/false", setlist_notes: "optional free-text setlist / recollection notes" } },
  { name: "delete_show", description: "Delete a logged show, identified by artist/venue/city text (the app asks the user to confirm first).", params: { term: "which show — artist, venue, or city text as the user wrote it" } },
  { name: "filter", description: "Search / list logged shows by artist, venue, or city.", params: { term: "optional free text to match against artist/venue/city" } },
  {
    name: "summarize",
    description:
      "The \"Year in Live Music\" recap: an OBSERVATIONAL-ONLY narration of shows ALREADY ATTENDED and spend ALREADY LOGGED (counts, most-seen artists, venues, spend split, longest gap). It NEVER recommends a show/artist/festival to attend, NEVER helps buy/sell/resell/price tickets, and NEVER acts as a marketplace. If the user is asking to buy, sell, resell, price tickets, or get a recommendation of what to attend, do not use this action.",
    params: {},
  },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "save_to_cloud", description: "Save a NOTE or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a piece of text. (To back up the WHOLE shows collection instead, use backup_save.)", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const recentShows = await store.listShows({ limit: 40 });
  const list = recentShows.map((s) => `${s.artists.join("/")} @ ${s.venue || "?"} (${s.city || "?"}, ${s.seen_on})`).join("; ");
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Logged shows (most recent first): ${list || "(none yet)"}.`) +
    (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent }, onChunk);

  // The shared brain returns an ARRAY (G17): "log X and rate it 5" = two actions, run in order.
  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" });
    actions.push(one.action);
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asDate = (v: unknown): string | undefined => asStr(v)?.match(/\b(\d{4}-\d{2}-\d{2})\b/)?.[1];
const asBool = (v: unknown): boolean | undefined => {
  if (typeof v === "boolean") return v;
  const s = asStr(v)?.toLowerCase();
  if (s === "true" || s === "yes") return true;
  if (s === "false" || s === "no") return false;
  return undefined;
};
const asRating = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n >= 1 && n <= 5 ? Math.round(n) : undefined;
};

export async function toAction(r: ServerChat): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_show": {
      const artistsRaw = asStr(p.artists);
      if (!artistsRaw) return none(say || 'I need at least an artist — e.g. "log The National at Brixton in London on 2024-05-01".');
      const artists = splitList(artistsRaw);
      if (artists.length === 0) return none(say || 'I need at least an artist — e.g. "log The National at Brixton in London on 2024-05-01".');
      const input: NewShow = { artists };
      const venue = asStr(p.venue); if (venue) input.venue = venue;
      const city = asStr(p.city); if (city) input.city = city;
      const seenOn = asDate(p.seen_on); if (seenOn) input.seen_on = seenOn;
      const fest = asBool(p.is_festival); if (fest !== undefined) input.is_festival = fest;
      // Cost / companions / rating were silently DROPPED before these params existed
      // (measured live 2026-08-19: "cost 65" logged a show with no cost, and the home's own
      // copy promises cost + companions). Cost arrives in major units; the store keeps cents.
      const costRaw = p.cost != null ? String(p.cost) : "";
      if (costRaw) { const cents = parseMoneyToCents(costRaw); if (cents !== null) input.cost = cents; }
      const comps = asStr(p.companions); if (comps) input.companions = splitList(comps);
      const rating = asRating(p.rating); if (rating) input.rating = rating;
      return { reply: say, action: { kind: "add_show", input, label: artists.join(", ") } };
    }
    case "edit_show": {
      const term = asStr(p.term);
      if (!term) return none(say || "Which show do you want to change?");
      const rows = await findShows(term);
      if (rows.length === 0) return none(await showsNotFound(term));
      if (rows.length > 1) return none(`Multiple shows match "${term}" — be more specific.`);
      const patch: ShowPatch = {};
      const rating = asRating(p.rating); if (rating) patch.rating = rating;
      const artistsRaw = asStr(p.artists); if (artistsRaw) { const a2 = splitList(artistsRaw); if (a2.length) patch.artists = a2; }
      const venue = asStr(p.venue); if (venue) patch.venue = venue;
      const city = asStr(p.city); if (city) patch.city = city;
      const seenOn = asDate(p.seen_on); if (seenOn) patch.seen_on = seenOn;
      const costRaw = p.cost != null ? String(p.cost) : "";
      if (costRaw) { const cents = parseMoneyToCents(costRaw); if (cents !== null) { patch.cost = cents; patch.cost_set = true; } }
      const comps = asStr(p.companions); if (comps) patch.companions = splitList(comps);
      const fest = asBool(p.is_festival); if (fest !== undefined) patch.is_festival = fest;
      const setlist = asStr(p.setlist_notes); if (setlist) patch.setlist_notes = setlist;
      if (Object.keys(patch).length === 0) return none(say || "What should I change — rating, the lineup, venue, city, date, cost, or who you went with?");
      return { reply: say, action: { kind: "edit_show", id: rows[0].id, patch, label: rows[0].artists.join(", ") } };
    }
    case "delete_show": {
      const term = asStr(p.term);
      if (!term) return none(say || "Which show do you want to delete?");
      const rows = await findShows(term);
      if (rows.length === 1) return { reply: say, action: { kind: "delete_show", id: rows[0].id, label: rows[0].artists.join(", ") } };
      if (rows.length === 0) return none(await showsNotFound(term));
      return none(`Multiple shows match "${term}" — be more specific.`);
    }
    case "filter": {
      const term = asStr(p.term) ?? "";
      return { reply: "", action: { kind: "filter", term, label: say || undefined } };
    }
    case "summarize":
      return { reply: say, action: { kind: "summarize" } };
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? asStr(p.contentKind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = none(HELP); return { reply: r.reply, actions: [r.action] }; }
  // THE FENCE — pre-LLM, free, on BOTH paths. A buy/sell/resell/price/recommend ask never
  // reaches EITHER billed call (routing OR recap).
  const fenced = fenceCheck(text);
  if (fenced) { const r = none(fenced); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below still
    // answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- OFFLINE / OUT-OF-FUNDS FALLBACK (free, on-device, no network) --------------
export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };

  // THE FENCE — deterministic refusal before anything else (Encore is observational-only).
  const fenced = fenceCheck(t);
  if (fenced) return { reply: "", action: { kind: "none", message: fenced } };

  const low = t.toLowerCase();

  // ADD
  const addM = t.match(/^(?:add|log|record|new|saw|went\s+to)\s+(.+)$/i);
  if (addM) {
    const input = parseAdd(addM[1]);
    if (!input) {
      return {
        reply: "",
        action: { kind: "none", message: 'I need at least an artist — e.g. "log The National at Brixton in London on 2024-05-01".' },
      };
    }
    return { reply: "", action: { kind: "add_show", input, label: input.artists.join(", ") } };
  }

  // SUMMARIZE
  if (/\b(summar(?:y|ise|ize)|overview|recap|year in (?:live )?music)\b/i.test(low)) {
    return { reply: "", action: { kind: "summarize" } };
  }

  // RATE (edit) — "rate <name> <1-5>"
  const rateM = t.match(/^(?:rate|rating)\s+(.+?)\s+([1-5])(?:\s*(?:stars?|★|\/\s*5))?\s*$/i);
  if (rateM) {
    const rows = await findShows(rateM[1]);
    if (rows.length === 1) {
      return { reply: "", action: { kind: "edit_show", id: rows[0].id, patch: { rating: Number(rateM[2]) }, label: rows[0].artists.join(", ") } };
    }
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await showsNotFound(rateM[1]) } };
    return { reply: "", action: { kind: "none", message: `Multiple shows match "${rateM[1].trim()}" — be more specific.` } };
  }

  // DELETE (HITL — Chat.tsx confirms before it runs)
  const delM = t.match(/^(?:delete|remove|drop)\s+(?:the\s+)?(.+)$/i);
  if (delM) {
    const rows = await findShows(delM[1]);
    if (rows.length === 1) return { reply: "", action: { kind: "delete_show", id: rows[0].id, label: rows[0].artists.join(", ") } };
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await showsNotFound(delM[1]) } };
    return { reply: "", action: { kind: "none", message: `Multiple shows match "${delM[1].trim()}" — be more specific.` } };
  }

  // FILTER / SEARCH
  if (QUERY_RE.test(low)) {
    const term = t
      .replace(/\b(show me|shows?|find|list|search|which|filter|how\s+many|all|by|see|seen|my|the|at|in|for)\b/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
    return { reply: "", action: { kind: "filter", term, label: t } };
  }

  return { reply: "", action: { kind: "none", message: HELP } };
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
